import clsx from 'clsx';
import {
  Hand,
  DownloadSimple,
  BookOpen,
  DeviceMobile,
  Envelope,
  FilePdf,
  Lock,
  XLogo,
  MagnifyingGlassPlus,
  FileMagnifyingGlassIcon,
  Queue,
  Car,
  Notepad,
  Notebook,
  NotionLogo,
  Cube,
  BracketsCurly,
  PushPin,
  TumblrLogo,
  Key,
  StarFour,
  CreditCard,
  Keyboard,
  UserCircle,
  SquaresFour,
  CheckCircle,
  Scales,
  Code,
  Lifebuoy,
  Copyright,
  Info,
  Folder,
  Highlighter,
  PlusCircle,
  Warning,
  BookmarkSimpleIcon,
  type Icon as PhosphorIcon,
  type IconWeight,
} from '@phosphor-icons/react';
import Icon, { type IconProps } from '../ui/Icon';
import booxLogo from '@/assets/img/boox-logo.png';
import kindleLogo from '@/assets/img/kindle-logo.png';
import koboLogo from '@/assets/img/kobo-logo.png';

// Brand icons that are raster logos rather than monochrome line art. Unlike the
// brand SVGs (which inherit `--color-icon`), these render as a rounded image
// badge in the logo's own colors on its opaque background, app-icon style.
const imageIconMap: Record<string, { src: string; alt: string }> = {
  kindle: { src: kindleLogo, alt: 'Kindle' },
  kobo: { src: koboLogo, alt: 'Kobo' },
  boox: { src: booxLogo, alt: 'Boox' },
};

// Phosphor icons referenced by docs page frontmatter `icon:` values. Only the
// icons we actually use are imported by name so the bundle stays tree-shakeable.
// Names are PascalCase, which never collides with the kebab-case brand icon set
// — that's how DocPageIcon decides which renderer to use.
const phosphorIconMap: Record<string, PhosphorIcon> = {
  Hand,
  DownloadSimple,
  BookOpen,
  DeviceMobile,
  Envelope,
  FilePdf,
  Lock,
  XLogo,
  MagnifyingGlassPlus,
  FileMagnifyingGlassIcon,
  Queue,
  Car,
  Notepad,
  Notebook,
  NotionLogo,
  Cube,
  BracketsCurly,
  PushPin,
  TumblrLogo,
  Key,
  StarFour,
  CreditCard,
  Keyboard,
  UserCircle,
  SquaresFour,
  CheckCircle,
  Scales,
  Code,
  Lifebuoy,
  Copyright,
  Info,
  Folder,
  Highlighter,
  PlusCircle,
  Warning,
  BookmarkSimpleIcon,
};

interface DocPageIconProps {
  name: string;
  className?: string;
  size?: 'sm' | 'md';
  /**
   * Phosphor stroke weight. Defaults to `light` to keep the stroke thin next to
   * the brand icon set; callers can pass a heavier weight (e.g. to match an
   * active nav item's bold label). No-op for raster brand icons.
   */
  weight?: IconWeight;
}

/**
 * Renders a docs page icon from its frontmatter `icon:` value, resolving to
 * either a custom Instapaper brand icon or a Phosphor icon. Brand icons are
 * preferred wherever Instapaper already owns the concept; Phosphor fills the
 * long tail. Phosphor renders at `light` weight to keep the stroke thin
 * next to the brand set rather than looking heavy and chunky.
 */
function DocPageIcon({
  name,
  className,
  size = 'md',
  weight = 'light',
}: DocPageIconProps) {
  const imageIcon = imageIconMap[name];

  if (imageIcon) {
    const px = size === 'sm' ? 14 : 20;
    return (
      <img
        src={imageIcon.src}
        alt=""
        width={px}
        height={px}
        // Match the brand/Phosphor footprint (14/20px) but soften the opaque
        // logo into an app-icon badge with rounded corners.
        className={clsx(
          'inline-block rounded-[4px] object-cover align-middle',
          className,
        )}
        aria-hidden
      />
    );
  }

  const PhosphorComponent = phosphorIconMap[name];

  if (PhosphorComponent) {
    return (
      <PhosphorComponent
        weight={weight}
        size={size === 'sm' ? 14 : 20}
        // Match the brand `@utility icon` color (--color-icon) rather than
        // inheriting the surrounding text color, so Phosphor and brand icons
        // render in the same tone. Phosphor maps `color` to the svg `fill`.
        color="var(--color-icon)"
        className={clsx('inline-block align-middle', className)}
        aria-hidden
      />
    );
  }

  return <Icon name={name as IconProps['name']} className={className} size={size} />;
}

export default DocPageIcon;
